import { DEFAULT_BANNERS, DEFAULT_PARTNERS, DEFAULT_SETTINGS, DEFAULT_SOCIAL, DEFAULT_TEAM } from '@/lib/defaults'
import { lumi } from '@/lib/lumi'
import type { Banner, Partner, RadioSettings, SocialLink, TeamMember } from '@/types/radio'
import { useCallback, useEffect, useState } from 'react'

async function seedCollection<T extends object>(name: 'radio_settings' | 'team_members' | 'banners' | 'social_links' | 'partners', items: T[]) {
  const collection = lumi.entities[name]
  await Promise.all(items.map((item) => collection.create(item)))
}

export function useRadioData() {
  const [settings, setSettings] = useState<RadioSettings | null>(DEFAULT_SETTINGS)
  const [team, setTeam] = useState<TeamMember[]>(DEFAULT_TEAM)
  const [banners, setBanners] = useState<Banner[]>(DEFAULT_BANNERS)
  const [social, setSocial] = useState<SocialLink[]>(DEFAULT_SOCIAL)
  const [partners, setPartners] = useState<Partner[]>(DEFAULT_PARTNERS)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const refresh = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const [settingsRes, teamRes, bannersRes, socialRes, partnersRes] = await Promise.all([
        lumi.entities.radio_settings.list(),
        lumi.entities.team_members.list({ sort: { order: 1 } }),
        lumi.entities.banners.list({ sort: { order: 1 } }),
        lumi.entities.social_links.list({ sort: { order: 1 } }),
        lumi.entities.partners.list({ sort: { order: 1 } }),
      ])

      const settingsList = (settingsRes?.list as RadioSettings[]) || []
      const teamList = (teamRes?.list as TeamMember[]) || []
      const bannersList = (bannersRes?.list as Banner[]) || []
      const socialList = (socialRes?.list as SocialLink[]) || []
      const partnersList = (partnersRes?.list as Partner[]) || []

      if (!settingsList.length) await seedCollection('radio_settings', [DEFAULT_SETTINGS])
      if (!teamList.length) await seedCollection('team_members', DEFAULT_TEAM)
      if (!bannersList.length) await seedCollection('banners', DEFAULT_BANNERS)
      if (!socialList.length) await seedCollection('social_links', DEFAULT_SOCIAL)
      if (!partnersList.length) await seedCollection('partners', DEFAULT_PARTNERS)

      const seeded = !settingsList.length || !teamList.length || !bannersList.length || !socialList.length || !partnersList.length
      if (seeded) {
        const [s2, t2, b2, so2, p2] = await Promise.all([
          lumi.entities.radio_settings.list(),
          lumi.entities.team_members.list({ sort: { order: 1 } }),
          lumi.entities.banners.list({ sort: { order: 1 } }),
          lumi.entities.social_links.list({ sort: { order: 1 } }),
          lumi.entities.partners.list({ sort: { order: 1 } }),
        ])
        setSettings(((s2?.list as RadioSettings[]) || [])[0] || DEFAULT_SETTINGS)
        setTeam(((t2?.list as TeamMember[]) || []).length ? (t2.list as TeamMember[]) : DEFAULT_TEAM)
        setBanners(((b2?.list as Banner[]) || []).length ? (b2.list as Banner[]) : DEFAULT_BANNERS)
        setSocial(((so2?.list as SocialLink[]) || []).length ? (so2.list as SocialLink[]) : DEFAULT_SOCIAL)
        setPartners(((p2?.list as Partner[]) || []).length ? (p2.list as Partner[]) : DEFAULT_PARTNERS)
        return
      }

      setSettings(settingsList[0] || DEFAULT_SETTINGS)
      setTeam(teamList)
      setBanners(bannersList)
      setSocial(socialList)
      setPartners(partnersList)
    } catch (err) {
      console.error('Falha ao carregar dados da rádio:', err)
      setSettings(DEFAULT_SETTINGS)
      setTeam(DEFAULT_TEAM)
      setBanners(DEFAULT_BANNERS)
      setSocial(DEFAULT_SOCIAL)
      setPartners(DEFAULT_PARTNERS)
      setError('Não foi possível carregar os dados da rádio.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  return { settings, team, banners, social, partners, loading, error, refresh }
}
